import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flame, RotateCcw, CheckCheck } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { api, fmtErr } from "../../lib/api";

const NEXT = { new: "cooking", cooking: "ready", ready: "served" };
const PREV = { cooking: "new", ready: "cooking", served: "ready" };
const STATUS_COLORS = {
  new: "bg-amber-400/10 text-amber-500 border-amber-400/30",
  cooking: "bg-blue-400/10 text-blue-500 border-blue-400/30",
  ready: "bg-emerald-400/10 text-emerald-500 border-emerald-400/30",
};

export default function KitchenTab({ restaurant }) {
  const [tickets, setTickets] = useState([]);

  const load = () => api.get(`/restaurants/${restaurant.id}/kitchen`).then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [restaurant.id]);

  const bump = async (oid, iid, status) => {
    try {
      await api.patch(`/restaurants/${restaurant.id}/orders/${oid}/items/${iid}`, { status });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const elapsed = (created) => {
    const mins = Math.max(0, Math.round((Date.now() - new Date(created).getTime()) / 60000));
    return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  return (
    <div data-testid="kitchen-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Flame className="w-4 h-4 text-primary" /> Live tickets · auto-refreshes every 15s
        </p>
        <button data-testid="kitchen-refresh" onClick={load} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {tickets.map((t) => (
          <div key={t.id} data-testid={`ticket-${t.id}`} className="rounded-2xl border border-border/60 bg-card p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-outfit font-semibold text-lg">{t.table_label || t.label || "Counter"}</h3>
              <span className="text-xs text-muted-foreground">{elapsed(t.created_at)}</span>
            </div>
            <div className="mt-4 space-y-2.5">
              {t.items.map((i) => (
                <div key={i.id} data-testid={`ticket-item-${i.id}`} className="rounded-xl border border-border/40 px-3.5 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{i.qty}× {i.name}</p>
                    <Badge variant="outline" className={STATUS_COLORS[i.status]}>{i.status}</Badge>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="text-xs text-muted-foreground">
                      {i.station}{i.seat ? ` · seat ${i.seat}` : ""}{i.notes && <span className="text-amber-500"> · {i.notes}</span>}
                    </p>
                    <div className="flex gap-1.5">
                      {PREV[i.status] && (
                        <button data-testid={`recall-${i.id}`} onClick={() => bump(t.id, i.id, PREV[i.status])}
                          title="Recall" className="w-7 h-7 rounded-lg border border-border/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button data-testid={`bump-${i.id}`} onClick={() => bump(t.id, i.id, NEXT[i.status])}
                        title={NEXT[i.status] === "served" ? "Bump (served)" : `Mark ${NEXT[i.status]}`}
                        className="w-7 h-7 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:opacity-90 transition-opacity">
                        <CheckCheck className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {tickets.length === 0 && (
        <div className="rounded-2xl border border-border/60 bg-card p-16 text-center text-muted-foreground" data-testid="no-tickets-msg">
          <Flame className="w-8 h-8 mx-auto mb-3 opacity-40" />
          All clear — no active tickets.
        </div>
      )}
    </div>
  );
}
